import { InFilterDefinition } from '@factorialco/f0-react'
import {
  InboxTasksAffecteesConnectionDocument,
  InboxTasksAffecteesConnectionQuery,
  TasksValueObjectsTaskCategoryEnum,
  TasksValueObjectsTaskKindEnum,
  TasksValueObjectsTaskStatusEnum,
} from 'generated/resources/graphql'

import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import { resolveEmployeeDisplayName } from 'modules/core/lib/employeeName'

const extractConnection = (data: InboxTasksAffecteesConnectionQuery) =>
  data.tasks.inboxTasksAffecteesConnection

const extractNodes = (connection: ReturnType<typeof extractConnection>) =>
  connection.edges.map((edge) => edge.node)

type Node = NonNullable<ReturnType<typeof extractNodes>>[number]

type Params = {
  label: string
  categories?: TasksValueObjectsTaskCategoryEnum[]
  kinds?: TasksValueObjectsTaskKindEnum[]
  statuses?: TasksValueObjectsTaskStatusEnum[]
}

export const useInboxTasksAffecteesFilter = ({
  label,
  categories,
  kinds,
  statuses,
}: Params): InFilterDefinition<string> => {
  const source = useGraphqlDataSource(
    {
      paginationType: 'infinite-scroll',
      fetchPolicy: 'cache-first',
      document: InboxTasksAffecteesConnectionDocument,
      perPage: 25,
      search: { enabled: true },
      mapData: (data) => {
        const connection = extractConnection(data)
        return extractNodes(connection)
      },
      mapPagination: (data) => {
        const connection = extractConnection(data)
        const pageInfo = connection.pageInfo
        const hasNextPage = !!pageInfo.hasNextPage

        const total = connection.totalCount ?? 0

        return {
          total,
          cursor: pageInfo.endCursor ?? '0',
          hasNextPage,
        }
      },
      mapVariables: ({ search }) => ({
        categories,
        kinds,
        search,
        statuses,
      }),
    },
    [categories, kinds, statuses]
  )

  return {
    label,
    type: 'in' as const,
    options: {
      source,
      mapOptions: (affectee: Node) => ({
        label: resolveEmployeeDisplayName(affectee),
        value: affectee.id,
      }),
    },
  }
}
